import { useRef, useState } from "react"

import { DashboardCustomize, Delete } from "@mui/icons-material"
import {
  Button,
  Popover,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material"

import { Analytics, ANALYTICS_EVENTS } from "../../../analytics"

import styles from "./CustomViewPicker.module.css"

interface CustomViewPickerProps {
  customViews: string[]
  onAddCustomView: (name: string) => void
  onDeleteCustomView: (name: string) => void
  onLoadCustomView: (name: string) => void
  getViewEventData: (name: string) => Record<string, string>
  getCurrentViewEventData: (name?: string) => Record<string, string>
}

export const CustomViewPicker = ({
  customViews,
  onAddCustomView,
  onDeleteCustomView,
  onLoadCustomView,
  getViewEventData,
  getCurrentViewEventData,
}: CustomViewPickerProps) => {
  const anchorElRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [creatingCustomView, setCreatingCustomView] = useState<boolean>(false)
  const [newCustomViewName, setNewCustomViewName] = useState<string>("")

  const clearAddCustomView = () => {
    setCreatingCustomView(false)
    setNewCustomViewName("")
  }

  const addCustomView = () => {
    if (newCustomViewName === "") {
      return
    }
    onAddCustomView(newCustomViewName)
    clearAddCustomView()
  }

  return (
    <>
      <div className={styles.customizeButton} ref={anchorElRef}>
        <IconButton title="Customize" color="secondary" size="small" onClick={() => setIsOpen(!isOpen)}>
          <DashboardCustomize />
        </IconButton>
      </div>
      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={anchorElRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div className={styles.customViewPickerContainer}>
          <div className={styles.createCustomViewContainer}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                width: "100%",
                paddingBottom: "10px",
              }}
            >
              Click here to create a custom view with your current configuration.
            </Typography>
            <div className={styles.addCustomViewButton}>
              {creatingCustomView ? (
                <>
                  <TextField
                    variant="outlined"
                    label="Custom View Name"
                    size="small"
                    sx={{ width: "100%" }}
                    autoFocus
                    value={newCustomViewName}
                    onChange={(e) => {
                      setNewCustomViewName(e.target.value)
                    }}
                  />
                  <div className={styles.addCustomViewActions}>
                    <div className={styles.addCustomViewAction}>
                      <Analytics
                        component={Button}
                        eventName={ANALYTICS_EVENTS.CUSTOM_VIEW_CREATED}
                        eventData={() => getCurrentViewEventData(newCustomViewName)}
                        variant="contained"
                        disabled={newCustomViewName === ""}
                        onClick={addCustomView}
                      >
                        Save
                      </Analytics>
                    </div>
                    <div className={styles.addCustomViewAction}>
                      <Button variant="outlined" onClick={clearAddCustomView}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Button variant="contained" onClick={() => setCreatingCustomView(true)}>
                  Create Custom View
                </Button>
              )}
            </div>
          </div>
          <div>
            <List>
              {customViews
                .slice()
                .reverse()
                .map((name) => (
                  <ListItem
                    disablePadding
                    key={name}
                    secondaryAction={
                      <Analytics
                        component={IconButton}
                        eventName={ANALYTICS_EVENTS.CUSTOM_VIEW_DELETED}
                        eventData={() => getViewEventData(name)}
                        edge="end"
                        aria-label="delete"
                        onClick={() => onDeleteCustomView(name)}
                      >
                        <Delete />
                      </Analytics>
                    }
                  >
                    <Analytics
                      component={ListItemButton}
                      eventName={ANALYTICS_EVENTS.CUSTOM_VIEW_LOADED}
                      eventData={() => getViewEventData(name)}
                      onClick={() => onLoadCustomView(name)}
                    >
                      <ListItemText>{name}</ListItemText>
                    </Analytics>
                  </ListItem>
                ))}
            </List>
          </div>
        </div>
      </Popover>
    </>
  )
}
